import { useEffect, useReducer } from 'react'

import {
  transformHomepageBlockResponse,
  HomepageBlocksModel,
} from '@marketplace-web/home/home-page-data'
import { isResponseError } from '@marketplace-web/core-api/api-client-util'

import { logHomeError, logHomeMessage, observeFetchDuration } from '../utils/client-observability'
import { useHomeContext } from '../HomeProvider'
import { getHomepageBlocks } from '../utils/api'
import { blocksReducer } from '../utils/blocks-reducer'
import useColumnCount from './useColumnCount'
import useTabs from './useTabs'

type Props = {
  initialData: HomepageBlocksModel
}

const useFetchHomepageBlocks = (props: Props) => {
  const { currentTab } = useTabs()
  const { homepageSessionId } = useHomeContext()
  const columnCount = useColumnCount()

  const [state, dispatch] = useReducer(blocksReducer, {
    isLoading: false,
    error: null,
    blocks: props.initialData,
  })

  useEffect(() => {
    dispatch({ type: 'success', payload: props.initialData })
  }, [props.initialData])

  const fetchMoreBlocks = async () => {
    const nextPageToken = state.blocks?.nextPageToken
    if (!nextPageToken) return

    dispatch({ type: 'more-blocks-loading' })

    const start = performance.now()
    const response = await getHomepageBlocks({
      tab: currentTab,
      homepageSessionId,
      nextPageToken,
      columnCount,
    })

    const isError = isResponseError(response)
    observeFetchDuration(performance.now() - start, { isError })

    if (isError) {
      dispatch({ type: 'more-blocks-failure' })
      logHomeMessage(
        `Blocks error: failed to load more blocks. ${response.message}`,
        `tab: ${currentTab.name}`,
      )

      return
    }

    dispatch({
      type: 'more-blocks-success',
      payload: transformHomepageBlockResponse(response, logHomeError),
    })
  }

  return {
    blocks: state.blocks,
    fetchMoreBlocks,
    areBlocksLoading: state.isLoading,
    error: state.error,
  }
}

export default useFetchHomepageBlocks
